import type { DealerReportAnswer } from "@hakili/contracts";
import { formatDate, formatMoney, formatPlate, formatQuantity, t } from "@hakili/i18n";
import {
  Avatar,
  Button,
  Card,
  Chip,
  ERROR_FIELD_BORDER,
  ERROR_TEXT,
  Field,
  METADONNEE,
  TEXTE_SECONDAIRE,
  TEXTE_TERTIAIRE,
} from "@hakili/ui";
import { AlertCircle, CheckCircle2, FileText, Info, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { ConfirmDialog } from "./confirm-dialog.js";
import { type DealerSettlement, SettlementCard } from "./settlement-card.js";
import { PriceGapCard } from "./price-gap-card.js";
import { CarTabPanel, CarTabStrip, useCarTab } from "./vehicle-tabs.js";
import { ReadFailure } from "./read-failure.js";
import { AvailabilityCard, type AvailabilityView, availabilityFrom } from "./availability-card.js";
import { ReportCard, reportFrom, type VehicleReportView } from "./report-card.js";

import type {
  DealerSession,
  PriceGapView,
  DoorResult,
  MakeReference,
  TeamMember,
  VehicleDetail,
  VehicleOptions,
  VehiclePhoto,
} from "../session/useSession.js";
import { type EquipmentReference, NO_EQUIPMENT } from "./equipment.js";
import { Gallery } from "./gallery.js";
import type { DossierRequestDoors, VehiclePaperDoors } from "../session/vehicle-papers.js";
import { DossierRequestsCard } from "./dossier-requests-card.js";
import { PhotoSlots } from "./photo-slots.js";
import { VehiclePapersCard } from "./vehicle-papers-card.js";
import { NO_VIEWS, type PhotoViews } from "./photo-views.js";
import { chipLabel, chipTone, EXITED_STAGES, MAINLINE, stageShort } from "./stages.js";
import { NO_MAKES, NO_OPTIONS, placeIsComplete, VehicleFields } from "./vehicle-fields.js";
import { priceCeilingLine, priceCeilingOf, priceLockOf } from "./vehicle-price-view.js";

/**
 * `P2-DLW-004` and `WF-DLR-03`, round I-A: **the vehicle's own page.**
 *
 * ## One page, two places
 *
 * On a wide screen this renders in the inventory's right-hand pane; on a narrow
 * one it is its own route. **It is the same component in both**, which is what
 * makes the split a rendering choice rather than a second screen , and what
 * keeps a deep link working: `/vehicules/:id` addresses the car whether or not
 * the list is beside it.
 *
 * ## The stepper shows the path TRAVELLED
 *
 * 2.2.1's eleven stages are a machine, and this round moves a car to exactly one
 * of them , `enligne`. So the stepper renders the path travelled and the stage
 * the car stands at, and **no future nodes**: `I20`'s rule for a terminal branch
 * is that zero future nodes render, and the same honesty applies before the
 * machine can move at all. Drawing ten grey circles a dealer cannot reach would
 * be a promise this round did not build.
 *
 * ## The journal is the door's, in the door's order
 *
 * `P1-CAT-004` writes a line as a side effect of the listing. The page renders
 * what the door returns, newest first, and composes nothing , a screen that
 * invented « Créé le … » would be writing history.
 */

/**
 * `F2`, the founder's reading of the pairs: **the drawing's « Progression ».**
 *
 * What stood here was a five-name list , `enligne`, `expertise`, `reserve`,
 * `paye`, `retire` , that carried a `reserve` the enum has never had and left
 * three real stages out. `P0-CAT-026` moved the vocabulary to `stages.ts` and
 * this file kept its own copy anyway, which is exactly the defect that item
 * was minted for, alive in a fourth place.
 *
 * It reads `MAINLINE` now, and there is nothing left here to drift.
 */

/**
 * The stage vocabulary moved to `stages.ts` at `round F`, `F2`.
 *
 * **It lived here AND in `vehicles.tsx`, character for character**, and the
 * dashboard would have made a third copy. All three carried the same eight
 * entries of an eleven-value enum, the same label for a `reserve` stage the api
 * cannot send, and the same four missing , so the same defect was present three
 * times and fixable in none of them. `P1-UIK-027`'s rule: the lists may differ,
 * the vocabulary may not.
 */

export interface VehiclePageProps {
  readonly id: string;
  readonly readVehicle: (id: string) => Promise<DoorResult<VehicleDetail>>;
  /** `F2.3`: the gallery the drawing puts at the top of this page. */
  readonly listPhotos: (
    vehicleId: string,
  ) => Promise<DoorResult<{ photos: readonly VehiclePhoto[]; missingRecommended: readonly string[] }>>;
  readonly removePhoto: (vehicleId: string, photoId: string) => Promise<DoorResult<void>>;
  /** `A-DLR`, Arc A: the vehicle's own papers. Optional so a surface without them renders as before. */
  readonly papers?: VehiclePaperDoors | undefined;
  /** `A-DLR`, Arc B: « Pièces demandées » , the dealer's open requests on this car. */
  readonly requests?: DossierRequestDoors | undefined;
  /** `round F3`, `W3`: the page takes photographs, for any car this dealer owns. */
  readonly listPhotoViews: () => Promise<DoorResult<PhotoViews>>;
  readonly uploadPhoto: (input: {
    vehicleId: string;
    view: string;
    file: File;
  }) => Promise<DoorResult<{ id: string }>>;
  readonly publishVehicle: (vehicleId: string) => Promise<DoorResult<{ publishedAt: string }>>;
  /**
   * `round F4`: the car's own changes, as the drawing draws them on this page.
   *
   * `WF-DLR-03` puts « Modifier le prix » and « Retirer du catalogue » here and
   * `F` left them dead with their reason in a `title`. They are alive now, and
   * « Remettre en ligne » stands where the architect put it: on a car that has
   * left the catalogue, and nowhere else.
   */
  readonly editVehicle?: (
    id: string,
    input: Record<string, unknown>,
  ) => Promise<DoorResult<{ changed: boolean }>>;
  readonly changePrice?: (
    id: string,
    priceAmount: number,
  ) => Promise<DoorResult<{ priceAmount: number; previousAmount: number }>>;
  readonly withdrawVehicle?: (id: string) => Promise<DoorResult<{ stage: string }>>;
  readonly relistVehicle?: (id: string) => Promise<DoorResult<{ stage: string }>>;
  readonly listVehicleMakes?: () => Promise<DoorResult<MakeReference>>;
  readonly listVehicleOptions?: () => Promise<DoorResult<VehicleOptions>>;
  readonly listEquipment?: () => Promise<DoorResult<EquipmentReference>>;
  readonly listCities?: () => Promise<DoorResult<readonly { id: string; name: string }[]>>;
  readonly listCityZones?: (
    cityId: string,
  ) => Promise<DoorResult<readonly { code: string; label: string }[]>>;
  /**
   * `round F6`, `OD-15`: **« Suivi par », and the manager's change of it.**
   *
   * Absent for an employee, and its absence is the whole rule: the founder gave
   * the hand-over to the manager, and the door refuses anyone else in its own
   * words regardless of what this screen renders.
   */
  readonly assignVehicle?: (
    id: string,
    userId: string,
  ) => Promise<DoorResult<{ id: string; fullName: string }>>;
  /** The team, for the list the change control offers. `OD-10`: both rungs may read it. */
  readonly listTeam?: () => Promise<DoorResult<readonly TeamMember[]>>;
  /** The list is the other half of the same fact, and re-reads itself. */
  readonly onChanged?: () => void;
  /** `T-2` (WF-DLR-11): the settlement standing on this car, the dealer's leg, the non-receipt, the handover. */
  readonly readSettlements?: () => Promise<DoorResult<readonly DealerSettlement[]>>;
  readonly confirmReceipt?: (settlementId: string, receivedOn: string) => Promise<DoorResult<unknown>>;
  readonly reportNonReceipt?: (settlementId: string) => Promise<DoorResult<unknown>>;
  readonly handoverVehicle?: (vehicleId: string, voucherReference: string) => Promise<DoorResult<unknown>>;
  /** `T-2b`, `M1` (`P2-DLW-008`): the dealer's step at the control, the card above the settlement's. */
  readonly readAvailability?: (vehicleId: string) => Promise<DoorResult<unknown>>;
  readonly readReport?: (vehicleId: string) => Promise<DoorResult<DealerReportAnswer>>;
  readonly confirmAvailability?: (vehicleId: string) => Promise<DoorResult<unknown>>;
  /** `PRICE-GAP-DEALER`, M1: the gap on the car's open file, and the seller's three answers to it. */
  readonly readPriceGap?: DealerSession["readPriceGap"];
  readonly answerPriceGap?: DealerSession["answerPriceGap"];
  readonly naviguer?: (to: string) => void;
}

export function VehiclePage({
  id,
  readVehicle,
  listPhotos,
  removePhoto,
  papers,
  requests,
  listPhotoViews,
  uploadPhoto,
  publishVehicle,
  editVehicle,
  changePrice,
  withdrawVehicle,
  relistVehicle,
  listVehicleMakes,
  listVehicleOptions,
  listEquipment,
  assignVehicle,
  listTeam,
  listCities,
  listCityZones,
  onChanged,
  readSettlements,
  confirmReceipt,
  reportNonReceipt,
  handoverVehicle,
  readAvailability,
  readReport,
  confirmAvailability,
  readPriceGap,
  answerPriceGap,
  naviguer,
}: VehiclePageProps): React.JSX.Element {
  const [car, setCar] = useState<VehicleDetail | null>(null);
  const [settlement, setSettlement] = useState<DealerSettlement | null>(null);
  const [availability, setAvailability] = useState<AvailabilityView | null>(null);
  const [report, setReport] = useState<VehicleReportView | null>(null);
  // `PRICE-GAP-DEALER`, M1: the gap on the car's open file, `null` when none stands.
  const [priceGap, setPriceGap] = useState<PriceGapView | null>(null);
  /** `A4` part 2, DEALER-READ-FAILURE-MONEY: the door's own message for each read that FAILED (a read that answered none is not here). */
  const [lectures, setLectures] = useState<
    Partial<Record<"settlement" | "availability" | "report" | "gap", string>>
  >({});
  const [erreur, setErreur] = useState<string | null>(null);
  /**
   * `round F6`: the « Suivi par » block's own two facts , whether the list of
   * colleagues is open, and who they are.
   *
   * The team is read ONCE, when the manager first opens the list. A car's page
   * that fetched a team on every render would be asking a question nobody
   * asked; a page that never fetched it could offer no names at all.
   */
  const [suiviOuvert, setSuiviOuvert] = useState(false);
  const [team, setTeam] = useState<readonly TeamMember[]>([]);
  const [photos, setPhotos] = useState<readonly VehiclePhoto[]>([]);
  const [missing, setMissing] = useState<readonly string[]>([]);
  /** `W3`: the view vocabulary, and which view's bytes are in flight. */
  const [views, setViews] = useState<PhotoViews>(NO_VIEWS);
  /** `A-DLR`, Arc B: bumped when a paper is recorded, so « Pièces demandées » reads again. */
  const [askedAgain, setAskedAgain] = useState(0);
  /**
   * `round F4`: **a refused GESTURE is not a page that failed to load.**
   *
   * `erreur` replaces the whole page with a card, which is right when the car
   * could not be read and wrong for everything else: a dealer told « un
   * acheteur s'est engagé » would lose the car they were looking at, and the
   * sentence would be the only thing on the screen. This one renders beside
   * the gestures, where the person pressed.
   */
  const [refus, setRefus] = useState<string | null>(null);
  /** `round F5`: the same refusal, carrying the field the door named. */
  const [fieldRefusal, setFieldRefusal] = useState<{ field: string; message: string } | undefined>(undefined);
  /** `round F4`: the price field, open or shut, and what is typed in it. */
  const [priceOpen, setPriceOpen] = useState(false);
  /** `G-D1`: the price door's refusal, the SERVER's sentence, drawn beside the field and cleared when the dealer types again. */
  const [priceRefus, setPriceRefus] = useState<string | null>(null);
  /** `G-D1`: the refusal was a LOCK (the race: the lock was not known at load). Once the page re-reads and the info box says it, the refusal is not drawn a second time. */
  const [priceRefusIsLock, setPriceRefusIsLock] = useState(false);
  const [priceTyped, setPriceTyped] = useState("");
  /** `G-D4`: the open tab, from the URL (`?onglet=`), and the way to change it. */
  const [tab, selectTab] = useCarTab(naviguer);
  /** `round F4`: the edit form, and the fields it holds while it is open. */
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [chosenEquipment, setChosenEquipment] = useState<readonly string[]>([]);
  const [makes, setMakes] = useState<MakeReference>(NO_MAKES);
  const [options, setOptions] = useState<VehicleOptions>(NO_OPTIONS);
  const [equipment, setEquipment] = useState<EquipmentReference>(NO_EQUIPMENT);
  const [cities, setCities] = useState<readonly { id: string; name: string }[]>([]);
  const [places, setPlaces] = useState<readonly { code: string; label: string }[]>([]);
  const [busyView, setBusyView] = useState<string | null>(null);
  const [failedView, setFailedView] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void listPhotoViews().then((answer) => {
      if (alive && answer.ok) setViews(answer.value);
    });
    return () => {
      alive = false;
    };
  }, [listPhotoViews]);

  /**
   * The gallery, re-read whenever the car changes.
   *
   * A separate read from the car's own, because a photograph's URL is signed at
   * read time and lives fifteen minutes: folding it into the vehicle's response
   * would tie the car's cacheability to a credential's lifetime.
   */
  const loadPhotos = useCallback(async (): Promise<void> => {
    const answer = await listPhotos(id);
    if (!answer.ok) return;
    setPhotos(answer.value.photos);
    setMissing(answer.value.missingRecommended);
  }, [id, listPhotos]);

  useEffect(() => {
    void loadPhotos();
  }, [loadPhotos]);

  /**
   * The vocabulary the edit form types against, read only when it opens.
   *
   * A car's page is opened to LOOK at a car; three reference reads on every
   * open would be three requests a person never uses, on a handset paying for
   * each one.
   */
  useEffect(() => {
    if (!editing) return undefined;
    const mounted = { yes: true };
    void Promise.all([
      listVehicleMakes?.() ?? Promise.resolve(null),
      listVehicleOptions?.() ?? Promise.resolve(null),
      listEquipment?.() ?? Promise.resolve(null),
      listCities?.() ?? Promise.resolve(null),
    ]).then(([reference, offered, suggested, covered]) => {
      if (!mounted.yes) return;
      if (reference?.ok === true) setMakes(reference.value);
      if (offered?.ok === true) setOptions(offered.value);
      if (suggested?.ok === true) setEquipment(suggested.value);
      if (covered?.ok === true) setCities(covered.value);
    });
    return () => {
      mounted.yes = false;
    };
  }, [editing, listVehicleMakes, listVehicleOptions, listEquipment, listCities]);

  /** `R64`, rule three: the quartier list is the CITY's, and follows it. */
  const chosenCity = cities.find((entry) => entry.name === (fields["city"] ?? ""));
  useEffect(() => {
    if (chosenCity === undefined || listCityZones === undefined) {
      setPlaces([]);
      return;
    }
    /** A reply for a city already left is stale: the last city CHOSEN wins, not the last answer to ARRIVE. */
    const arrival = { current: true };
    void listCityZones(chosenCity.id).then((answer) => {
      if (arrival.current) setPlaces(answer.ok ? answer.value : []);
    });
    return () => {
      arrival.current = false;
    };
  }, [chosenCity, listCityZones]);

  const charger = useCallback(async (): Promise<void> => {
    const answer = await readVehicle(id);
    if (answer.ok) {
      setCar(answer.value);
      setErreur(null);
    } else {
      setCar(null);
      setErreur(answer.message);
    }
    // `A4` part 2: a read that FAILED is said (« Impossible de lire … »), never drawn as an absent card; one that answered none draws nothing.
    const failed: Partial<Record<"settlement" | "availability" | "report" | "gap", string>> = {};
    // `T-2`: the settlement on this car, by the dealer's own door; none, no card.
    if (readSettlements !== undefined) {
      const settlements = await readSettlements();
      setSettlement(settlements.ok ? (settlements.value.find((one) => one.vehicleId === id) ?? null) : null);
      if (!settlements.ok) failed.settlement = settlements.message;
    }
    // `T-2b`: the control's checklist on this car, by the dealer's own door; no demand, no card.
    if (readAvailability !== undefined) {
      const read = await readAvailability(id);
      setAvailability(read.ok ? availabilityFrom(read.value) : null);
      if (!read.ok) failed.availability = read.message;
    }
    // Lane E, M2: the report standing on this car (paper and Hakili's sheet), by the dealer's own door; none, no card.
    if (readReport !== undefined) {
      const read = await readReport(id);
      setReport(read.ok ? reportFrom(read.value) : null);
      if (!read.ok) failed.report = read.message;
    }
    // `PRICE-GAP-DEALER`, M1: the gap and the seller's answer to it , none, the card is not drawn.
    if (readPriceGap !== undefined) {
      const gap = await readPriceGap(id);
      setPriceGap(gap.ok ? gap.value : null);
      if (!gap.ok) failed.gap = gap.message;
    }
    setLectures(failed);
  }, [readVehicle, readSettlements, readAvailability, readReport, readPriceGap, id]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /**
   * `round F3`, `W3`: **a photograph chosen here goes at once.**
   *
   * The panel holds its files because the car does not exist yet; here it does,
   * so there is nothing to wait for. Choosing a slot that already holds a
   * photograph REPLACES it: the old row is removed first, because the view's
   * partial unique index admits one photograph per named view and the second
   * attach would be refused with a message about a constraint.
   */
  const choose = useCallback(
    async (view: string, file: File): Promise<void> => {
      setBusyView(view);
      setFailedView(null);
      try {
        const standing = photos.find((photo) => photo.view === view);
        if (standing !== undefined) {
          const removed = await removePhoto(id, standing.id);
          if (!removed.ok) {
            setFailedView(view);
            setErreur(removed.message);
            return;
          }
        }
        const sent = await uploadPhoto({ vehicleId: id, view, file });
        if (!sent.ok) {
          setFailedView(view);
          setErreur(sent.message);
          return;
        }
        setErreur(null);
        await loadPhotos();
      } finally {
        setBusyView(null);
      }
    },
    [id, photos, removePhoto, uploadPhoto, loadPhotos],
  );

  /**
   * **« Mettre en ligne » on the page**, for a car that was born without one.
   *
   * `WF-DLR-02` refuses a car with no photograph, and its refusal is shown
   * where the gesture is , not at the top of a list two panes away, which is
   * where a person who pressed a button does not look.
   */
  const publish = useCallback(async (): Promise<void> => {
    const answer = await publishVehicle(id);
    if (!answer.ok) {
      setErreur(answer.message);
      return;
    }
    setErreur(null);
    await charger();
  }, [id, publishVehicle, charger]);

  /**
   * `P0-CAT-031`: **« Modifier le prix », and the guard the drawing writes.**
   *
   * `WF-DLR-04`: *« Enregistrer le prix » is disabled while the field is empty
   * or equal to the current price*. The guard is here as well as in the
   * aggregate and in `ck_vehicle_price_history__actually_changed`, and that is
   * not three copies of one rule: this one exists so the button is grey rather
   * than so the write is refused, and a person is told before they press.
   */
  const savePrice = async (): Promise<void> => {
    if (changePrice === undefined || car === null) return;
    const answer = await changePrice(id, Number(priceTyped));
    if (!answer.ok) {
      setPriceRefus(answer.message);
      const isLock = answer.refusal?.kind === "locked";
      setPriceRefusIsLock(isLock);
      // The race: the lock was not known at load. Re-read, so the info box serves the sentence and the field stops accepting attempts.
      if (isLock) await charger();
      return;
    }
    setRefus(null);
    setPriceRefus(null);
    setPriceRefusIsLock(false);
    setPriceOpen(false);
    setPriceTyped("");
    await charger();
    onChanged?.();
  };

  /** `A3`, D-1029-3: a price change asks first too (old and new price), and writes nothing until « Confirmer ». */
  const [confirmingPrice, setConfirmingPrice] = useState(false);

  /** `A3`, DEALER-CONFIRMS: « Retirer » asks first, and writes nothing until the dealer confirms. */
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);

  /** R's last review of the strip: « Retirer » on a gallery photograph asks first (the photograph's id, or none). The door it calls is the one it always called. */
  const [confirmingPhoto, setConfirmingPhoto] = useState<string | null>(null);

  /** `P0-CAT-032`: « Retirer du catalogue ». The refusal is the api's own. */
  const withdraw = async (): Promise<void> => {
    if (withdrawVehicle === undefined) return;
    const answer = await withdrawVehicle(id);
    if (!answer.ok) {
      setRefus(answer.message);
      return;
    }
    setRefus(null);
    await charger();
    onChanged?.();
  };

  /** `P0-CAT-032`: « Remettre en ligne », which republishes in the same act. */
  const relist = async (): Promise<void> => {
    if (relistVehicle === undefined) return;
    const answer = await relistVehicle(id);
    if (!answer.ok) {
      setRefus(answer.message);
      return;
    }
    setRefus(null);
    await charger();
    onChanged?.();
  };

  /**
   * `P0-CAT-033`: the edit, sent as the fields the form holds.
   *
   * The price is not among them , it is not on this form, and the door refuses
   * it , and the reference goes only while the car is unpublished.
   */
  const saveEdit = async (): Promise<void> => {
    if (editVehicle === undefined || car === null) return;
    const answer = await editVehicle(id, {
      make: (fields["make"] ?? "").trim(),
      model: (fields["model"] ?? "").trim(),
      year: Number(fields["year"]),
      mileageKm: Number(fields["mileageKm"]),
      colour: fields["colour"] ?? "",
      plate: fields["plate"] ?? "",
      city: fields["city"] ?? "",
      district: fields["district"] ?? "",
      gearbox: fields["gearbox"] ?? "",
      fuel: fields["fuel"] ?? "",
      ...(fields["seats"] === undefined || fields["seats"] === "" ? {} : { seats: Number(fields["seats"]) }),
      equipment: chosenEquipment,
      ...(car.published ? {} : { stockReference: fields["stockReference"] ?? "" }),
    });
    if (!answer.ok) {
      setRefus(answer.message);
      // Beside the field, where the door named one. The founder read a `400`
      // about a quartier with the quartier out of sight.
      setFieldRefusal(
        answer.field === undefined ? undefined : { field: answer.field, message: answer.message },
      );
      return;
    }
    setRefus(null);
    setFieldRefusal(undefined);
    setEditing(false);
    await charger();
    onChanged?.();
  };

  /** The form opens on the car as it stands, never on an empty form. */
  const openEdit = (): void => {
    if (car === null) return;
    setFields({
      stockReference: car.stockReference,
      make: car.make,
      model: car.model,
      year: String(car.year),
      mileageKm: String(car.mileageKm),
      colour: car.colour,
      // A form field holds text; absence is the empty box, and the door turns
      // it back into NULL. `optionalPlate` is the one place that decision lives.
      plate: car.plate ?? "",
      city: car.city,
      district: car.district,
      gearbox: car.gearbox ?? "",
      fuel: car.fuel ?? "",
      seats: car.seats === null ? "" : String(car.seats),
    });
    setChosenEquipment(car.equipment);
    setEditing(true);
  };

  /**
   * `round F6`: the colleagues a car may be handed to.
   *
   * Read when the block opens and never before. Somebody already deactivated
   * is not offered , the door refuses them in words, and offering a name the
   * api will refuse is the defect `P0-CNW-030` was minted for.
   */
  useEffect(() => {
    if (!suiviOuvert || listTeam === undefined || team.length > 0) return;
    void listTeam().then((answer) => {
      if (answer.ok) setTeam(answer.value.filter((member) => member.status === "actif"));
      else setErreur(answer.message);
    });
  }, [suiviOuvert, listTeam, team.length]);

  if (erreur !== null) {
    return (
      <Card>
        <p role="alert" className="text-sm text-rose-600" data-testid="vehicule-page-erreur">
          {erreur}
        </p>
      </Card>
    );
  }

  if (car === null) {
    return (
      <Card>
        <p className="text-sm text-gray-500" data-testid="vehicule-page-chargement">
          {t("common.state.chargement")}
        </p>
      </Card>
    );
  }

  /** `G-D1`: the opening price the read serves, or `null` (no dossier open, or the key not served yet). */
  const ceiling = priceCeilingOf(car);
  /** `G-D1` (`LOCK-STATE`): why the price is locked, from the read. */
  const lock = priceLockOf(car);
  /** The caption of the photograph the removal dialog is about: its served view name, or none (a photograph stored without a view). */
  const confirmingCaption =
    confirmingPhoto === null ? null : (photos.find((photo) => photo.id === confirmingPhoto)?.view ?? null);
  /** `G-D1` (R's ruling on 44863b0b): a lock SERVED means the dealer cannot attempt: the field is read-only, the submit inactive, both described by the info box. */
  const priceLocked = lock !== null;
  /** What the locked controls point at: the info box, when it serves the sentence; nothing otherwise. */
  const lockDescribedBy = lock?.sentence === undefined ? undefined : "vehicule-page-prix-verrouille-phrase";
  /** The sentence is never shown twice: a lock refusal gives way to the info box once the box serves the sentence. */
  const refusShown = priceRefus !== null && !(priceRefusIsLock && lock?.sentence !== undefined);

  /**
   * **The stepper's two shapes, and the drawing draws both.**
   *
   * On the mainline: all eight nodes, the ones behind marked done, the current
   * one lit with its ring, the ones ahead grey , the drawing shows the whole
   * progression, because a seller wants to know what is coming as much as
   * where he stands.
   *
   * On a terminal branch: the path travelled up to the stage the branch leaves
   * from, then the branch itself as a rose node with a cross , and **no future
   * nodes at all**. `I20`'s rule, and the mockup's own comment: a car that
   * falls into a branch has no next stage, so drawing one would be a promise
   * the machine cannot keep.
   */
  /**
   * `round F4`: **the exit stage is the RECORD's now.**
   *
   * `F3` read a map on this side , the branch's declared exit , and it said
   * « withdrawn from `enligne` » for a car withdrawn from `expertise`. The
   * column `stage_before` is the answer to *where was it*, the withdrawal door
   * writes it, `ck_vehicles__terminal_has_stage_before` guarantees it is there,
   * and the approximation is deleted rather than kept beside it.
   */
  // `?? null` and not a bare read: a row written before this column existed
  // sends nothing, and `undefined` would fall through the `=== null` below and
  // draw a stepper one node long.
  // G5-3: `stage_before` is stamped on EVERY transition (a car at `expertise`
  // carries `enligne`), so it is the exit point only for a car that has left
  // the line. A car on the mainline draws no failure mark.
  const exited = EXITED_STAGES.has(car.stage) ? (car.stageBefore ?? null) : null;
  const reached = MAINLINE.findIndex((stage) => stage.id === (exited ?? car.stage));
  const shown = exited === null ? MAINLINE : MAINLINE.slice(0, Math.max(reached, 0) + 1);

  /** `OD-15`: the hand-over itself. The list re-reads, because it shows it too. */
  const confierA = async (userId: string): Promise<void> => {
    if (assignVehicle === undefined) return;
    const answer = await assignVehicle(id, userId);
    if (!answer.ok) {
      setErreur(answer.message);
      return;
    }
    setSuiviOuvert(false);
    await charger();
    onChanged?.();
  };

  return (
    <Card>
      <div data-testid="vehicule-page">
        {/* `G-D4` (`P1-DLW-055`), R's batches 4 and 7: the stage area (the price row, the lock, what could not be read, the publication, the progression) stays above the tabs; each section lives in its tab. */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-medium text-gray-900" data-testid="vehicule-page-titre">
              {car.title}
            </h1>
            <p className="mt-1 text-xs text-[color:var(--hk-text-2)]" data-testid="vehicule-page-identite">
              {/* `R65` and `R69`: a count is not money, and a plate is rendered
                  as the record spells it. The STORED value stays normalised ,
                  `ux_vehicles__plate_live` needs one spelling , and this is the
                  reading, which is what a person checks against the papers. */}
              {/* `round F4`: **the plate is shown only when there is one.**
                  The founder's decision made it optional, and « V-004 ·  · 62
                  400 km » with a hole where a plate would be reads as a screen
                  that lost something rather than as a car that has none. */}
              {car.stockReference}
              {car.plate === null || car.plate === "" ? null : <> · {formatPlate(car.plate)}</>} ·{" "}
              {formatQuantity(car.mileageKm)} km
              {/**
               * `round F3`, `W4`: **the date is the PUBLICATION's, and it is
               * absent until there is one.**
               *
               * It read `listed_on` , the day the row was written , so a car
               * nobody had published carried « en ligne depuis le … » on its
               * own page while its card said « Non publié » two panes away.
               */}
              {car.publishedAt !== null ? (
                <>
                  {" · "}
                  {/* The drawing's own line (`hakili-dealer.jsx:2534`): « en
                    ligne depuis le 9 août ». A DATE here and a count of days on
                    the card , the same fact said the way each place needs it,
                    which is why the two sentences are two keys. */}
                  <span data-testid="vehicule-page-enligne">
                    {t("dealer.vehicules.enLigneDepuisLe")} {formatDate(new Date(car.publishedAt))}
                  </span>
                </>
              ) : null}
            </p>
          </div>
          {/**
           * **The price on the RIGHT of the title, and large** , the drawing's
           * own arrangement (`hakili-dealer.jsx:2526-2543`), found on the pair
           * of the fiche's lower half.
           *
           * It is not decoration. The price is the fact a dealership looks for
           * first when it opens a car, and under the title it sat below the
           * identity line, level with nothing, competing with the mileage.
           * Beside the title it is the second thing on the page.
           */}
          <div className="shrink-0 text-right">
            <p className="text-2xl font-semibold tabular-nums" data-testid="vehicule-page-prix">
              {formatMoney(car.priceAmount)}
            </p>
            <div className="mt-1 flex justify-end">
              <Chip tone={chipTone(car)}>{chipLabel(car)}</Chip>
            </div>
            {/* `G-D1`: the price the open dossier was opened at, SERVED (`priceCeiling`, `B3-PRICING-1`'s contract). LAYOUT HELD to the iteration-2 frames (`design/dealer-v2` `12c346b2`): the words and the logic
                are built, the arrangement is the designer's to confirm. */}
            {lock === null ? null : (
              <p className="mt-1" data-testid="vehicule-page-prix-verrouille" data-reason={lock.reason}>
                <Chip tone="attention">{t("dealer.vehicules.prixVerrouille")}</Chip>
              </p>
            )}
            {ceiling === null ? null : (
              <p className="mt-1 max-w-xs text-xs text-gray-500" data-testid="vehicule-page-prix-plafond">
                {priceCeilingLine(ceiling).opening}
                {priceCeilingLine(ceiling).margin === null
                  ? null
                  : ` ${priceCeilingLine(ceiling).margin ?? ""}`}
              </p>
            )}
          </div>
        </div>
        {/* `PRICELOCK-SENTENCE` (#900), Designer R / H's frame 16: the sentence the READ serves, in an info box, full width, directly under the price row and its chip. Drawn as served: the page composes nothing, and
            a lock served without one (an older answer) draws the chip alone. Not a tooltip, not inside the chip. */}
        {lock?.sentence === undefined ? null : (
          <div
            id="vehicule-page-prix-verrouille-phrase"
            role="note"
            className="mt-2 flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-gray-900"
            data-testid="vehicule-page-prix-verrouille-phrase"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
            <span>{lock.sentence}</span>
          </div>
        )}
        {/* `T-2b` (WF-DLR-07, `P2-DLW-008`): the dealer's step at the control , the checklist, the advance the
            door will debit, the confirmation by his own door, the shortcut to the top-up. */}
        {(
          [
            ["gap", "dealer.lecture.ecart", "vehicule-echec-ecart"],
            ["availability", "dealer.lecture.controle", "vehicule-echec-controle"],
            ["report", "dealer.lecture.rapport", "vehicule-echec-rapport"],
            ["settlement", "dealer.lecture.reglement", "vehicule-echec-reglement"],
          ] as const
        ).map(([key, what, testId]) => {
          const message = lectures[key];
          return message === undefined ? null : (
            <div key={key} className="mt-4">
              <ReadFailure
                what={what}
                message={message}
                onRetry={() => {
                  void charger();
                }}
                testId={testId}
                retryTestId={`${testId}-reessayer`}
              />
            </div>
          );
        })}
        {car.published ? null : (
          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={() => {
                void publish();
              }}
              data-testid="vehicule-page-publier"
            >
              {t("dealer.vehicules.publier")}
            </Button>
            <span className={`text-xs ${TEXTE_SECONDAIRE}`}>{t("dealer.vehicules.nonPublieRaison")}</span>
          </div>
        )}
        {/* A card of its own, as the drawing has it: the stepper is a second
          subject on this page, not a paragraph of the first. */}
        {/**
         * `round F3`, `W5`: **`py-5`, not `py-4`.**
         *
         * The current node wears `ring-4`, and a ring is drawn OUTSIDE the
         * element's box. Four pixels of padding left the halo touching the
         * card's top edge and clipped where the card rounds. The founder saw
         * it and it is the sort of thing only a photograph shows.
         */}
        <div className="mt-5 rounded-2xl border border-gray-200 bg-white px-5 py-5">
          <h2 className="text-sm font-semibold text-gray-900">{t("dealer.vehicules.progression")}</h2>
          {/* The row scrolls INSIDE itself rather than widening the page: eight
          nodes at seventy-eight pixels do not fit a narrow pane, and a page
          that scrolls sideways is a page whose whole layout has broken. */}
          <div className="mt-2 overflow-x-auto pb-1">
            <ol className="flex items-start" data-testid="vehicule-page-stepper">
              {shown.map((stage, index) => {
                const done = exited !== null ? true : index < reached;
                const current = exited === null && index === reached;
                return (
                  <li key={stage.id} className="flex items-start">
                    {index === 0 ? null : (
                      <span
                        className={`mt-2.5 h-px w-4 ${index <= reached ? "bg-indigo-400" : "bg-gray-200"}`}
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className="flex w-[78px] shrink-0 flex-col items-center gap-1 px-1"
                      data-stage={stage.id}
                      // English, per `09-01`: the drawing writes `data-etape`
                      // and `data-etat`, and those are the MOCKUP's attributes,
                      // read on the drawing's side of the pair. Ours are read by
                      // this app's own specs and are names, not wire values.
                      data-state={current ? "current" : done ? "done" : "ahead"}
                    >
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full ${
                          done
                            ? "bg-indigo-600"
                            : current
                              ? "bg-indigo-600 ring-4 ring-indigo-100"
                              : "bg-gray-200"
                        }`}
                      >
                        {done ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
                        )}
                      </span>
                      <span
                        className={`text-center text-xs leading-tight font-medium ${
                          current
                            ? "text-indigo-600"
                            : done
                              ? "text-gray-600"
                              : "text-[color:var(--hk-text-2)]"
                        }`}
                      >
                        {/* The lit node reads what the chip reads: a lock is an overlay, never a stage,
                          and « En ligne » under a « Réservé » chip was the progression and the chip
                          disagreeing about one car (V-005, 16 October). */}
                        {current ? chipLabel(car, true) : stageShort(stage.id)}
                      </span>
                    </span>
                  </li>
                );
              })}
              {exited === null ? null : (
                <li className="flex items-start" data-stage={car.stage} data-state="terminal">
                  <span className="mt-2.5 h-px w-4 bg-rose-300" aria-hidden="true" />
                  <span className="flex w-[78px] shrink-0 flex-col items-center gap-1 px-1">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-rose-500 ring-4 ring-rose-100">
                      <X className="h-3 w-3 text-white" aria-hidden="true" />
                    </span>
                    <span className="text-center text-xs leading-tight font-medium text-rose-600">
                      {stageShort(car.stage)}
                    </span>
                  </span>
                </li>
              )}
            </ol>
          </div>
        </div>

        <CarTabStrip tab={tab} onSelect={selectTab} />

        <CarTabPanel tab="vente" active={tab}>
          {/**
           * `P0-DLW-032`, round F4: **the drawing's gestures, alive.**
           *
           * `F2.3` drew « Modifier le prix » and « Retirer du catalogue » greyed,
           * with their reason in a `GuardedButton` , the doors did not exist.
           * They exist now, so the buttons act, and `I19`'s narrowing goes with
           * the reason it carried: a gesture that works needs no explanation of
           * why it does not.
           *
           * **Which gestures a car offers is the STAGE's answer, not this
           * screen's.** A car on the mainline may be edited, repriced and
           * withdrawn; one that has left the catalogue may only be put back. The
           * api refuses either way and in its own words , these conditions are so
           * that a person is not offered a button that will refuse them.
           */}
          {priceGap !== null && answerPriceGap !== undefined ? (
            <PriceGapCard
              view={priceGap}
              answer={(answer) => answerPriceGap(id, answer)}
              onChanged={() => {
                void charger();
                onChanged?.();
              }}
            />
          ) : null}
          {/* `T-2` (WF-DLR-11, `P2-DLW-011`, `P2-DLW-017`, `P2-DLW-012`): the settlement card once the payer
            was instructed , two rows, the dealer's declaration, the strip, the handover against the bon. */}
          {settlement !== null &&
          confirmReceipt !== undefined &&
          reportNonReceipt !== undefined &&
          handoverVehicle !== undefined ? (
            <SettlementCard
              settlement={settlement}
              confirmReceipt={confirmReceipt}
              reportNonReceipt={reportNonReceipt}
              handover={handoverVehicle}
              onChanged={() => {
                void charger();
                onChanged?.();
              }}
            />
          ) : null}
          <div className="mt-3 flex flex-col gap-2" data-testid="vehicule-page-gestes">
            {car.stage === "retireDuCatalogue" ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void relist();
                  }}
                  data-testid="vehicule-page-remettre"
                >
                  {t("dealer.vehicules.remettreEnLigne")}
                </Button>
                {/* The relist republishes in the same act, and says so before it
                  is pressed: a car back on sale is back on sale, and a dealer
                  who expected a draft would find an advert. */}
                <span className={`text-xs ${TEXTE_SECONDAIRE}`}>
                  {t("dealer.vehicules.remettreEnLigneNote")}
                </span>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    openEdit();
                  }}
                  data-testid="vehicule-page-modifier"
                >
                  {t("dealer.vehicules.modifier")}
                </Button>
                {priceOpen ? (
                  /**
                   * `WF-DLR-04`: *« Modifier le prix » expands a field*, and
                   * « Enregistrer le prix » is disabled while it is empty or
                   * equal to the current price. The fee note is the workflow's
                   * own sentence, and it is here rather than in a tooltip because
                   * the price a dealer types decides what a control costs.
                   */
                  <div
                    className="rounded-xl border border-gray-200 px-3 py-2.5"
                    data-testid="vehicule-page-prix-bloc"
                  >
                    <Field label={t("dealer.vehicules.prix")} htmlFor="vehicule-page-prix">
                      <input
                        id="vehicule-page-prix"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        // Designer R (review of cbad2c28): the value is GROUPED as it is typed, by the kit's formatter; the state keeps the digits.
                        value={priceTyped === "" ? "" : formatQuantity(Number(priceTyped))}
                        onChange={(event) => {
                          setPriceTyped(event.target.value.replace(/\D/gu, "").slice(0, 12));
                          setPriceRefus(null);
                        }}
                        readOnly={priceLocked}
                        aria-invalid={refusShown ? true : undefined}
                        aria-describedby={
                          lockDescribedBy ?? (refusShown ? "vehicule-page-prix-refus" : undefined)
                        }
                        className={`w-full rounded-xl border px-3 py-2 text-sm tabular-nums ${
                          refusShown
                            ? ERROR_FIELD_BORDER
                            : priceLocked
                              ? "border-[color:var(--hk-line)] bg-[color:var(--hk-surface-2)]"
                              : "border-[color:var(--hk-line-strong)]"
                        }`}
                        data-testid="vehicule-page-prix-champ"
                      />
                    </Field>
                    {!refusShown ? null : (
                      <p
                        id="vehicule-page-prix-refus"
                        role="alert"
                        className={`mt-1 flex items-start gap-1.5 text-sm ${ERROR_TEXT}`}
                        data-testid="vehicule-page-prix-refus"
                      >
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>{priceRefus}</span>
                      </p>
                    )}
                    <p
                      className="mt-1 text-[10px] text-[color:var(--hk-text-2)]"
                      data-testid="vehicule-page-prix-note"
                    >
                      {t("dealer.vehicules.prixNoteForfait")}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        // `WF-DLR-04`'s own guard: disabled while the field is
                        // empty or equal to the current price. `inactive` is the
                        // kit's word for it and it renders visibly unavailable ,
                        // a control that looks pressable and does nothing is
                        // worse than one that looks unavailable.
                        inactive={
                          priceLocked || priceTyped.trim() === "" || Number(priceTyped) === car.priceAmount
                        }
                        {...(lockDescribedBy === undefined ? {} : { "aria-describedby": lockDescribedBy })}
                        onClick={() => {
                          setConfirmingPrice(true);
                        }}
                        data-testid="vehicule-page-prix-enregistrer"
                      >
                        {t("dealer.vehicules.enregistrerPrix")}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setPriceOpen(false);
                          setPriceTyped("");
                          setPriceRefus(null);
                        }}
                        data-testid="vehicule-page-prix-annuler"
                      >
                        {t("common.action.annuler")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      // The field opens on the price the car actually has, so
                      // « different from the current price » is a change a person
                      // can see themselves making.
                      setPriceTyped(String(car.priceAmount));
                      setPriceOpen(true);
                    }}
                    data-testid="vehicule-page-modifier-prix"
                  >
                    {t("dealer.vehicules.modifierPrix")}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => {
                    setConfirmingWithdraw(true);
                  }}
                  data-testid="vehicule-page-retirer"
                >
                  {t("dealer.vehicules.retirerDuCatalogue")}
                </Button>
              </>
            )}
            {confirmingPrice ? (
              <ConfirmDialog
                title={t("dealer.confirmer.modifierPrixTitre")}
                body={t("dealer.confirmer.modifierPrixCorps")
                  .split("{ancien}")
                  .join(formatMoney(car.priceAmount))
                  .split("{nouveau}")
                  .join(formatMoney(Number(priceTyped)))}
                confirmLabel={t("dealer.confirmer.modifierPrixConfirmer")}
                onCancel={() => {
                  setConfirmingPrice(false);
                }}
                onConfirm={() => {
                  setConfirmingPrice(false);
                  void savePrice();
                }}
              />
            ) : null}
            {confirmingWithdraw ? (
              <ConfirmDialog
                title={t("dealer.confirmer.retirerTitre")}
                body={t("dealer.confirmer.retirerCorps").split("{titre}").join(car.title)}
                confirmLabel={t("dealer.vehicules.retirerDuCatalogue")}
                danger
                onCancel={() => {
                  setConfirmingWithdraw(false);
                }}
                onConfirm={() => {
                  setConfirmingWithdraw(false);
                  void withdraw();
                }}
              />
            ) : null}
            {refus === null ? null : (
              <p role="alert" className="text-sm text-rose-600" data-testid="vehicule-page-refus">
                {refus}
              </p>
            )}
          </div>
          {editing ? (
            /**
             * **The panel's own form, on the car.**
             *
             * One component (`vehicle-fields.tsx`), so the two forms cannot
             * disagree about what a car has. What differs is the door: no price
             * here, and the reference read-only once the car is published.
             */
            <form
              className="mt-3 space-y-3 rounded-xl border border-gray-200 px-3 py-3"
              autoComplete="off"
              data-testid="vehicule-page-formulaire"
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <VehicleFields
                values={fields}
                mode="edit"
                referenceLocked={car.published}
                vocabulary={{ makes, options, equipment, cities, places }}
                chosenEquipment={chosenEquipment}
                onToggleEquipment={(entry) => {
                  setChosenEquipment((current) =>
                    current.includes(entry) ? current.filter((kept) => kept !== entry) : [...current, entry],
                  );
                }}
                onChange={(key, value) => {
                  setFields((current) => ({ ...current, [key]: value }));
                }}
                // `round F5`: the door's own sentence, beside the field it names.
                refusal={fieldRefusal}
              />
              <div className="flex items-center gap-2">
                <Button
                  /**
                   * `round F5`: the gesture waits for the place to be whole. The
                   * founder met this exact button with the quartier unchosen.
                   *
                   * `FX-2b`, `M2 (a)` (`P2-DLW-039`): **and it now says so.** This was the
                   * one `FX-2a`'s `M1` left with its reason and a price , `Button`'s
                   * `inactive` on a DEALER surface, a word that component's own doc
                   * reserves for the client app while `I19`'s scope line names « the
                   * console, the dealer space and the bank app ». The distinction that
                   * bought it a row still holds and is why the LINK is not copied here:
                   * the add panel's two place fields are PROPOSED from Profil, so its
                   * sentence offers the way there; the fiche's are the car's own, already
                   * saved, so the sentence stands alone. The KEY is the same, because the
                   * reason is the same reason.
                   */
                  inactive={!placeIsComplete(fields, places)}
                  onClick={() => {
                    void saveEdit();
                  }}
                  data-testid="vehicule-page-enregistrer"
                >
                  {t("common.action.enregistrer")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(false);
                    setRefus(null);
                  }}
                  data-testid="vehicule-page-annuler"
                >
                  {t("common.action.annuler")}
                </Button>
              </div>
              {/**
               * `FX-2b`, `M2 (a)`: the reason, where the person is looking.
               *
               * Under `W5`'s exception as `FX-2a` entered it , a primary action waiting on
               * a fact says why in words a reader and a camera can both see, and a reason
               * nobody hovers is no reason. `R81`: nothing at all once the place is whole,
               * because a sentence beside a live control is decoration and decoration is
               * what teaches a reader to stop reading.
               *
               * **Its own key, and the FRAME is what found that owed.** The first cure reused the add
               * panel's sentence, whose second clause reads « … ou dans votre Profil pour qu'ils
               * soient proposés à chaque véhicule » , true there, where the two fields ARE proposed
               * from Profil, and false here, where they are the car's own and already saved. The pair
               * showed it in one look. One reason, two screens, two sentences.
               */}
              {placeIsComplete(fields, places) ? null : (
                <p className="mt-2 text-sm text-gray-500" data-testid="vehicule-page-enregistrer-motif">
                  {t("dealer.vehicules.placeMotif")}
                </p>
              )}
            </form>
          ) : null}
          {/**
           * **The equipment, in the drawing's green** (`hakili-dealer.jsx:2546`).
           *
           * Absent when there is none, and no empty box in its place: a car with
           * no equipment listed is the ordinary case, and a heading standing over
           * nothing would read as a fact the door had failed to send.
           */}
          {car.equipment.length === 0 ? null : (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="vehicule-page-equipements">
              {car.equipment.map((entry) => (
                <span
                  key={entry}
                  className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
                >
                  {entry}
                </span>
              ))}
            </div>
          )}
          {/**
           * `round F6`, `OD-15`: **« Suivi par »**, the drawing's own block at
           * `hakili-dealer.jsx:2577-2600`.
           *
           * The name and the role in words, the face beside it, and , for the
           * manager alone , « Changer ». The founder's rule: *le responsable peut
           * le confier à qui il veut à tout moment*.
           *
           * « personne pour l'instant » is the drawing's own sentence for a car
           * that follows nobody, which is every car listed before this column
           * existed. It is a fact and not a defect, so it is said plainly.
           */}
          <div
            className="mt-5 rounded-2xl border border-gray-200 bg-white px-5 py-4"
            data-testid="vehicule-suivi-bloc"
          >
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-gray-900">{t("dealer.vehicules.suiviPar")}</h2>
                <p className={`mt-0.5 ${METADONNEE} ${TEXTE_SECONDAIRE}`} data-testid="vehicule-suivi-nom">
                  {car.assigneeName ?? t("dealer.vehicules.suiviPersonne")}
                </p>
              </div>
              {(car.assigneeName ?? null) === null ? null : (
                <Avatar name={car.assigneeName ?? ""} size="sm" />
              )}
              {assignVehicle === undefined ? null : (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSuiviOuvert(!suiviOuvert);
                  }}
                  data-testid="vehicule-suivi-changer"
                >
                  {t("dealer.vehicules.suiviChanger")}
                </Button>
              )}
            </div>
            {assignVehicle === undefined || !suiviOuvert ? null : (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="vehicule-suivi-liste">
                {team.length === 0 ? (
                  <p className={`${METADONNEE} ${TEXTE_TERTIAIRE}`}>
                    {t("dealer.vehicules.suiviEquipeVide")}
                  </p>
                ) : (
                  team.map((membre) => (
                    <Button
                      key={membre.id}
                      variant="secondary"
                      inactive={membre.id === car.assigneeUserId}
                      onClick={() => {
                        void confierA(membre.id);
                      }}
                      data-testid={`vehicule-suivi-choix-${membre.id}`}
                    >
                      {membre.fullName}
                    </Button>
                  ))
                )}
              </div>
            )}
          </div>
        </CarTabPanel>
        <CarTabPanel tab="dossiers" active={tab}>
          {requests === undefined ? null : (
            <DossierRequestsCard key={`demandes-${String(askedAgain)}`} vehicleId={id} doors={requests} />
          )}
        </CarTabPanel>
        <CarTabPanel tab="expertise" active={tab}>
          {availability?.demandId !== null && availability !== null && confirmAvailability !== undefined ? (
            <AvailabilityCard
              view={availability}
              confirm={() => confirmAvailability(id)}
              onChanged={() => {
                void charger();
                onChanged?.();
              }}
              naviguer={naviguer ?? (() => undefined)}
            />
          ) : null}
          {/* Lane E, M2 (record of 9 September): the report standing on the car, opened by its seller , the
            cabinet's paper and Hakili's sheet, never the buyer's name nor the asked price nor the verdict. */}
          <ReportCard view={report} />
        </CarTabPanel>
        <CarTabPanel tab="photos" active={tab}>
          {/* **The gallery first**, as the drawing has it (`:1653`): a person
          opening a car looks at it before reading about it, and a buyer three
          hundred kilometres away has nothing else. */}
          <div className="mb-4">
            <Gallery
              photos={photos}
              missingRecommended={missing}
              onRemove={(photoId) => {
                setConfirmingPhoto(photoId);
              }}
            />
          </div>
          {/**
           * `round F3`, `W3`: **the same eight slots, on the car's own page.**
           *
           * A car born without pictures , which is every car whose upload failed
           * on the way in , could not receive any: the only control that took a
           * photograph lived in the panel that creates a car. Both of the
           * founder's cars were born that way and had to be finished from here.
           */}
          <div className="mb-4">
            <PhotoSlots
              views={views}
              content={Object.fromEntries(
                photos.map((photo, index) => [
                  photo.view ?? views.otherView,
                  {
                    url: photo.url,
                    rank: index + 1,
                    ...(photo.view === null ? {} : { caption: photo.view }),
                  },
                ]),
              )}
              failedView={failedView}
              busyView={busyView}
              count={photos.length}
              onChoose={(view, file) => {
                void choose(view, file);
              }}
              onRemove={(view) => {
                // The slots' cross opens the SAME confirmation as the gallery's « Retirer »: the photograph standing in that view.
                const standing = photos.find((photo) => photo.view === view);
                if (standing !== undefined) setConfirmingPhoto(standing.id);
              }}
            />
          </div>
        </CarTabPanel>
        <CarTabPanel tab="papiers" active={tab}>
          {papers === undefined ? null : (
            <VehiclePapersCard
              vehicleId={id}
              doors={papers}
              onChanged={() => {
                setAskedAgain((n) => n + 1);
              }}
            />
          )}
        </CarTabPanel>
        <CarTabPanel tab="journal" active={tab}>
          <h2 className="mt-6 flex items-center gap-2 text-sm font-medium text-gray-900">
            <FileText className="h-4 w-4 text-gray-400" aria-hidden="true" />
            {t("dealer.vehicules.journal")}
          </h2>
          <ul className="mt-2 space-y-2" data-testid="vehicule-page-journal">
            {car.journal.map((entry) => (
              <li key={`${entry.occurredAt}-${entry.body}`} className="text-sm text-gray-600">
                <span className="text-xs text-[color:var(--hk-text-2)]">
                  {formatDate(new Date(entry.occurredAt))}
                </span>{" "}
                {entry.body}
              </li>
            ))}
          </ul>
        </CarTabPanel>
        {/* A dialog is a fixed box: inside a hidden panel it would never be seen, so it stands outside the panels. */}
        {confirmingPhoto === null ? null : (
          <ConfirmDialog
            title={
              confirmingCaption === null
                ? t("dealer.vehicules.retirerPhotoTitreSansNom")
                : t("dealer.vehicules.retirerPhotoTitre").split("{legende}").join(confirmingCaption)
            }
            body={t("dealer.vehicules.retirerPhotoCorps")}
            confirmLabel={t("dealer.vehicules.retirerPhoto")}
            danger
            onCancel={() => {
              setConfirmingPhoto(null);
            }}
            onConfirm={() => {
              const photoId = confirmingPhoto;
              setConfirmingPhoto(null);
              void removePhoto(id, photoId).then((answer) => {
                if (answer.ok) {
                  setErreur(null);
                  void loadPhotos();
                } else setErreur(answer.message);
              });
            }}
          />
        )}
      </div>
    </Card>
  );
}
